import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";

const NotificationContext = createContext(null);
export const useNotifications = () => useContext(NotificationContext);

const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(523.25, now);
    gain1.gain.setValueAtTime(0.15, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    osc1.connect(gain1).connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.25);
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(659.25, now + 0.1);
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.linearRampToValueAtTime(0.12, now + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    osc2.connect(gain2).connect(ctx.destination);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.4);
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = "triangle";
    osc3.frequency.setValueAtTime(783.99, now + 0.15);
    gain3.gain.setValueAtTime(0, now);
    gain3.gain.linearRampToValueAtTime(0.06, now + 0.18);
    gain3.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    osc3.connect(gain3).connect(ctx.destination);
    osc3.start(now + 0.15);
    osc3.stop(now + 0.5);
    setTimeout(() => ctx.close(), 600);
  } catch (e) { /* AudioContext not available */ }
};

const NOTIF_TYPE_LABELS = {
  follow: "New Follower", like: "New Like", comment: "New Comment",
  reply: "New Reply", mention: "Mentioned You", donation: "Donation Received",
  pledge: "New Pledge", campaign_success: "Campaign Goal Reached",
  redemption_call: "Pledge Redemption", trust_penalty: "Trust Score Update",
  join: "Someone Joined", campaign_message: "Campaign Message",
  follow_update: "Activity Update",
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState("default");
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const lastSoundAt = useRef(0);
  const pushRegistered = useRef(false);

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    if (supported) setPushPermission(Notification.permission);
  }, []);

  const subscribeToPush = useCallback(async () => {
    if (!pushSupported || !user || pushRegistered.current) return;
    if (Notification.permission === "denied") return;
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        if (Notification.permission === "default") return;
        const keyRes = await axios.get(`${API}/push/vapid-key`);
        const vapidKey = keyRes.data.public_key;
        if (!vapidKey) return;
        const urlBase64ToUint8Array = (base64String) => {
          const padding = '='.repeat((4 - base64String.length % 4) % 4);
          const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
          const rawData = window.atob(base64);
          return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
        };
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }
      await axios.post(`${API}/push/subscribe`, { subscription: subscription.toJSON() }, { withCredentials: true });
      pushRegistered.current = true;
    } catch (e) { console.warn("Push subscription failed:", e); }
  }, [pushSupported, user]);

  useEffect(() => {
    if (user && pushSupported && Notification.permission === "granted") subscribeToPush();
  }, [user, pushSupported, subscribeToPush]);

  const requestPushPermission = useCallback(async () => {
    if (!pushSupported) return;
    try {
      const result = await Notification.requestPermission();
      setPushPermission(result);
      if (result === "granted") await subscribeToPush();
    } catch (e) { console.warn("Permission request failed:", e); }
  }, [pushSupported, subscribeToPush]);

  const connectWs = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/auth/ws-token`, { withCredentials: true });
      const token = res.data.token;
      if (!token) return;
      const wsProtocol = BACKEND_URL.startsWith("https") ? "wss" : "ws";
      const wsHost = BACKEND_URL.replace(/^https?:\/\//, "");
      const wsUrl = `${wsProtocol}://${wsHost}/api/ws/notifications?token=${token}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => { clearTimeout(reconnectTimer.current); };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "init") { setUnreadCount(data.unread_count); }
          else if (data.type === "notification") {
            setUnreadCount(data.unread_count);
            const now = Date.now();
            if (now - lastSoundAt.current > 2000) { lastSoundAt.current = now; playNotificationSound(); }
            const label = NOTIF_TYPE_LABELS[data.notification?.type] || "Notification";
            const msg = data.notification?.message || "";
            toast(label, { description: msg.length > 80 ? msg.slice(0, 80) + "…" : msg, duration: 5000 });
          } else if (data.type === "unread_count") { setUnreadCount(data.unread_count); }
        } catch (e) { /* ignore */ }
      };
      ws.onclose = () => { wsRef.current = null; reconnectTimer.current = setTimeout(connectWs, 5000); };
      ws.onerror = () => { ws.close(); };
    } catch (e) { reconnectTimer.current = setTimeout(connectWs, 10000); }
  }, [user]);

  useEffect(() => {
    if (user) { connectWs(); } else { setUnreadCount(0); if (wsRef.current) { wsRef.current.close(); wsRef.current = null; } }
    return () => { clearTimeout(reconnectTimer.current); if (wsRef.current) { wsRef.current.close(); wsRef.current = null; } };
  }, [user, connectWs]);

  useEffect(() => {
    const interval = setInterval(() => { if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send("ping"); }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <NotificationContext.Provider value={{ unreadCount, setUnreadCount, pushSupported, pushPermission, requestPushPermission }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const NotifBadge = () => {
  const ctx = useContext(NotificationContext);
  const count = ctx?.unreadCount || 0;
  if (count === 0) return null;
  return (
    <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 animate-in zoom-in-50 duration-200">
      {count > 99 ? "99+" : count}
    </span>
  );
};

export const PushPromptBanner = () => {
  const { user } = useAuth();
  const notifCtx = useContext(NotificationContext);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('push_prompt_dismissed') === '1');

  if (!user || !notifCtx?.pushSupported || notifCtx.pushPermission !== 'default' || dismissed) return null;

  const handleEnable = async () => { await notifCtx.requestPushPermission(); setDismissed(true); sessionStorage.setItem('push_prompt_dismissed', '1'); };
  const handleDismiss = () => { setDismissed(true); sessionStorage.setItem('push_prompt_dismissed', '1'); };

  return (
    <div data-testid="push-prompt-banner" className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-4 animate-in slide-in-from-bottom-4 duration-300">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-jeekota-primary/10 flex items-center justify-center flex-shrink-0">
          <Bell size={20} className="text-jeekota-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-jeekota-text">Stay in the loop</p>
          <p className="text-xs text-jeekota-text-secondary mt-0.5">Get notified about new followers, likes, comments, and donations — even when you're not on Jeekota.</p>
          <div className="flex gap-2 mt-3">
            <button onClick={handleEnable} data-testid="push-enable-btn" className="px-4 py-1.5 bg-jeekota-primary text-white text-xs font-semibold rounded-full hover:bg-jeekota-primary/90 transition-colors">Enable notifications</button>
            <button onClick={handleDismiss} data-testid="push-dismiss-btn" className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors">Not now</button>
          </div>
        </div>
        <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X size={16} /></button>
      </div>
    </div>
  );
};
